import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPropertyDefinition, deletePropertyDefinition, getAssetTypes, updatePropertyDefinition } from "../services/assetTypeService";
import "../styles/AssetTypes.css";

const emptyProperty = {
  propertyName: "",
  dataType: "string",
  isRequired: false,
  displayOrder: 0,
  is_public: true,
};

const AssetTypesPage = () => {
  const location = useLocation();
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedTypes, setExpandedTypes] = useState({});
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyForm, setPropertyForm] = useState(emptyProperty);
  const [savingProperty, setSavingProperty] = useState(false);
  const [deletingPropertyId, setDeletingPropertyId] = useState(null);
  const [bulkMessage] = useState(location.state?.uploadSuccess || "");

  useEffect(() => {
    loadAssetTypes();
  }, []);

  const loadAssetTypes = async () => {
    try {
      setAssetTypes(await getAssetTypes());
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load asset types.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (assetTypeId) => {
    setExpandedTypes((current) => ({
      ...current,
      [assetTypeId]: !current[assetTypeId],
    }));
  };

  const startAddProperty = (assetTypeId) => {
    setExpandedTypes((current) => ({ ...current, [assetTypeId]: true }));
    setEditingProperty({ assetTypeId });
    setPropertyForm(emptyProperty);
  };

  const startEditProperty = (assetTypeId, property) => {
    setEditingProperty({ assetTypeId, propertyId: property.id });
    setPropertyForm({
      propertyName: property.propertyName || "",
      dataType: property.dataType || "string",
      isRequired: Boolean(property.isRequired),
      displayOrder: property.displayOrder || 0,
      is_public: property.isPublic !== false,
    });
  };

  const handlePropertyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPropertyForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePropertySubmit = async (event) => {
    event.preventDefault();
    setSavingProperty(true);
    setError("");

    const payload = {
      ...propertyForm,
      assetTypeId: editingProperty.assetTypeId,
      displayOrder: Number(propertyForm.displayOrder),
    };

    try {
      if (editingProperty.propertyId) {
        await updatePropertyDefinition(editingProperty.propertyId, payload);
      } else {
        await createPropertyDefinition(payload);
      }
      setEditingProperty(null);
      await loadAssetTypes();
    } catch (requestError) {
      const messages = requestError.response?.data?.messages;
      setError(messages?.join(", ") || requestError.response?.data?.message || "Unable to save property definition.");
    } finally {
      setSavingProperty(false);
    }
  };

  const handleDeleteProperty = async (property) => {
    if (!window.confirm(`Delete property '${property.propertyName}'?`)) return;

    setDeletingPropertyId(property.id);
    setError("");
    try {
      await deletePropertyDefinition(property.id);
      await loadAssetTypes();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to delete property definition.");
    } finally {
      setDeletingPropertyId(null);
    }
  };

  if (loading) {
    return (
      <div className="loading loading-with-spinner">
        <span className="spinner" aria-hidden="true" />
        <span>Loading asset types...</span>
      </div>
    );
  }
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="asset-types-container">
      <div className="asset-types-header">
        <h1>Asset Types</h1>
        <div className="asset-types-actions">
          <Link to="/asset-types/upload" className="btn btn-secondary">
            + Bulk Upload
          </Link>
          <Link to="/asset-types/create" className="btn btn-primary">
            + Create Asset Type
          </Link>
        </div>
      </div>

      {bulkMessage && <div className="alert alert-success">{bulkMessage}</div>}

      {assetTypes.length === 0 ? (
        <div className="empty-state">
          <p>No asset types found.</p>
          <Link to="/asset-types/create" className="btn btn-primary">
            Create First Asset Type
          </Link>
        </div>
      ) : (
        <table className="asset-types-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Properties</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assetTypes.map((assetType) => (
              <React.Fragment key={assetType.id}>
                <tr>
                  <td>
                    <button className="expand-button" onClick={() => toggleExpanded(assetType.id)} aria-expanded={Boolean(expandedTypes[assetType.id])}>
                      {expandedTypes[assetType.id] ? "-" : "+"}
                    </button>
                    {assetType.name}
                  </td>
                  <td>{assetType.description || "-"}</td>
                  <td>{assetType.PropertyDefinitions?.length || 0}</td>
                  <td>
                    <Link to={`/asset-types/${assetType.id}/edit`} className="btn btn-sm btn-secondary">
                      Edit
                    </Link>
                    {/* <button className="btn btn-sm btn-primary property-add-button" onClick={() => startAddProperty(assetType.id)}>
                      Add Property
                    </button> */}
                  </td>
                </tr>
                {expandedTypes[assetType.id] && (
                  <tr className="property-row">
                    <td colSpan="4">
                      <div className="property-panel">
                        <div className="property-panel-header">
                          <strong>Property Definitions</strong>
                          <button className="btn btn-sm btn-primary" onClick={() => startAddProperty(assetType.id)}>
                            + Add Property
                          </button>
                        </div>
                        {assetType.PropertyDefinitions?.length ? (
                          <table className="properties-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Required</th>
                                <th>Public</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {assetType.PropertyDefinitions.map((property) => (
                                <tr key={property.id}>
                                  <td>{property.propertyName}</td>
                                  <td>{property.dataType}</td>
                                  <td>{property.isRequired ? "Yes" : "No"}</td>
                                  <td>{property.isPublic === false ? "No" : "Yes"}</td>
                                  <td>
                                    <button className="btn btn-sm btn-secondary" onClick={() => startEditProperty(assetType.id, property)}>
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger property-delete-button"
                                      onClick={() => handleDeleteProperty(property)}
                                      disabled={deletingPropertyId === property.id}
                                    >
                                      {deletingPropertyId === property.id ? (
                                        <>
                                          <span className="button-spinner" aria-hidden="true" />
                                          Deleting...
                                        </>
                                      ) : (
                                        "Delete"
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="small">No property definitions yet.</p>
                        )}
                        {editingProperty?.assetTypeId === assetType.id && (
                          <form className="property-form" onSubmit={handlePropertySubmit}>
                            <h3>{editingProperty.propertyId ? "Edit Property Definition" : "Add Property Definition"}</h3>
                            <div className="property-form-grid">
                              <div className="form-group">
                                <label htmlFor="propertyName">Name *</label>
                                <input id="propertyName" name="propertyName" value={propertyForm.propertyName} onChange={handlePropertyChange} maxLength="100" required />
                              </div>
                              <div className="form-group">
                                <label htmlFor="dataType">Data Type *</label>
                                <select id="dataType" name="dataType" value={propertyForm.dataType} onChange={handlePropertyChange} required>
                                  <option value="string">String</option>
                                  <option value="text">Text</option>
                                  <option value="integer">Integer</option>
                                  <option value="decimal">Decimal</option>
                                  <option value="date">Date</option>
                                  <option value="boolean">Boolean</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label htmlFor="displayOrder">Display Order</label>
                                <input id="displayOrder" name="displayOrder" type="number" min="0" value={propertyForm.displayOrder} onChange={handlePropertyChange} />
                              </div>
                            </div>
                            <label className="property-checkbox">
                              <input name="isRequired" type="checkbox" checked={propertyForm.isRequired} onChange={handlePropertyChange} /> Required
                            </label>
                            <label className="property-checkbox">
                              <input name="is_public" type="checkbox" checked={propertyForm.is_public} onChange={handlePropertyChange} /> Public
                            </label>
                            <div className="form-actions">
                              <button type="submit" className="btn btn-primary" disabled={savingProperty}>
                                {savingProperty ? (
                                  <>
                                    <span className="button-spinner" aria-hidden="true" />
                                    Saving...
                                  </>
                                ) : (
                                  "Save Property"
                                )}
                              </button>
                              <button type="button" className="btn btn-secondary" onClick={() => setEditingProperty(null)}>
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AssetTypesPage;
